<template>
    <div class="waffleGrid" :style="{'border-left-color': tierColor}"
         @mouseover="isHovering = true"
         @mouseout="isHovering = false">
        <div class="instanceMeta">
            <div>
                <div class="lock" v-if="isLocked">
                    <IconBase width="9" height="12" :iconColor="baseIconColor" iconName="lockIcon"/>
                </div>
                <div class="counter font-6" v-show="!isLocked && !isHovering && counter">
                    {{counter}}.
                </div>
                <div v-if="onUpdate">
                    <InlineLoader class="inlineLoader"></InlineLoader>
                </div>
                <div v-else class="handle" v-show="!isLocked && (!counter || isHovering)">
                    <IconBase width="8" height="17" :iconColor="baseIconColor"
                              iconName="dragHandleSmallIcon"
                              verticalAlign="middle"
                              class="middle"/>
                </div>
            </div>
            <div class="font-6 provider truncate">
                <span :class="{providerNameMissing: missingConfiguration}">
                    {{getProviderName}} •&nbsp;
                </span>
            </div>
            <div class="instanceName font-7 truncate">
                <Tooltip :text="name" class="truncate tooltip">
                    {{name}}
                </Tooltip>
            </div>
            <IconBase width="11" height="12" :iconColor="globeColor" iconName="countryIcon"
                      v-if="Array.isArray(activeCountries) && !isAllCountries"
                      v-tooltip="{content: activeCountriesTooltip}"
                      class="activeCountries"/>
        </div>
        <div class="rate" :class="{rateInheritedValue: hasRateValue}">
            <InlineInput v-model="rateData"
                         @changed-value="onRateChange"
                         :validation="$v"
                         :placeholder="ratePlaceholder"
                         attributeField="rateData"
                         attributeName="Rate"
                         :isDisabled="viewOnly || tierIsUpdating"
                         inputType="text"
                         overrideTooltipText="This rate was overridden for at least 1 country"
                         :valueOverridden="hasRateValue && isRateOverridden"
                         :hidePlaceholderWithValueOnFocus="defaultRatePlaceholder"
                         typingPreventHelper="NumbersOnly"
                         :confirmationHighlight="rateChangeOnGoing"
                         v-if="showRate">
                <span class="dollarSign">{{hasRateValue ? '$' : ''}}</span>
            </InlineInput>
        </div>
        <div class="ecpm values"
             :class="{'font-6': !hasRateValue, 'font-7 light800': hasRateValue || !ecpm}">
            {{ ecpmComputed }}
        </div>
        <div class="stat font-7 values" :class="{'light800': !impressions}">
            {{ impressionsComputed }}
        </div>
        <span class="disableDrag">
            <PercentBar class="sovData values"
                        :val="sov.val"
                        :maxVal="sov.maxVal || 0"
                        :fillColorAlpha="0.1"
                        noValueText="N/A"
                        :filledSpaceColor="consts.COLORS.PRIMARY_500"
            ></PercentBar>
            <div class="actions" v-if="!onUpdate">
                <v-popover trigger="click"
                           placement="left"
                           popoverInnerClass="tooltip-inner"
                           popoverBaseClass="vueTooltip"
                           popoverArrowClass="tooltip-arrow"
                           v-show="missingConfiguration">
                    <div class="missingConfigPopOver">
                        <IconBase width="12" height="12" :iconColor="missingConfigColor" iconName="wrenchIcon"
                                  v-show="missingConfiguration" class="missingConfig" />
                    </div>
                    <template slot="popover">
                        Input missing configurations on the
                        <a :href="'/partners/monetize/mediation/setup/app/' + appKey" v-once>
                            SDK Networks
                        </a> page
                    </template>
                </v-popover>
                <IconMenu :menuItems="menuItems"
                          triggerClass="menu-button"
                          :uniqueId="`${providerId}${instanceId}${tagId}`"
                          @actionClicked="onMenuActions">
                    <IconBase width="5" height="16" :iconColor="handleColor" iconName="moreIcon"
                              v-show="showHandle" :iconHoverColor="handleHoverColor"
                              display="grid"
                              class="more"/>
                </IconMenu>
            </div>
        </span>
    </div>
</template>

<script>
import Vue from 'vue';
import IconBase from 'components/IconBase.vue';
import InlineInput from 'components/inputs/inlineInput/InlineInput.vue';
import InlineLoader from 'components/loader/inlineLoader.vue';
import validators from 'services/libWrappers/vuelidate/validators';
import consts from 'services/metaData/consts';
import tooltip from 'services/libWrappers/vue-tooltip';
import decimalFormat from 'services/filters/decimalFormat';
import Tooltip from 'components/Tooltip/Tooltip.vue';
import IconMenu from 'components/iconMenu/IconMenu.vue';
import PercentBar from 'components/percentBar/PercentBar.vue';
import Countries from '../../services/countries';

const { between, decimal } = validators;

tooltip(Vue);

export default {
    name: 'instanceWaffle',
    props: {
        providerName: {
            type: String
        },
        name: {
            type: String
        },
        appKey: {
            type: String
        },
        tagType: {
            type: Number
        },
        tagId: {
            type: Number
        },
        tierId: {
            type: Number
        },
        instanceId: {
            type: Number
        },
        aniId: {
            type: Number
        },
        providerId: {
            type: Number
        },
        rates: {
            type: Object
        },
        ecpm: {
            type: Number
        },
        impressions: {
            type: Number
        },
        isLocked: {
            type: Boolean,
            default: false
        },
        counter: {
            type: Number,
            default: null
        },
        tierColor: {
            type: String,
            default: 'transparent'
        },
        activeCountries: {
            type: [Array, String]
        },
        showMenu: {
            type: Boolean,
            default: true
        },
        showRate: {
            type: Boolean,
            default: true
        },
        capping: {
            type: Number | null,
            default: null
        },
        missingConfiguration: {
            type: Boolean
        },
        isAllCountries: {
            type: Boolean
        },
        viewOnly: {
            type: Boolean
        },
        allowRemoveFromGroup: {
            type: Boolean,
            default: true
        },
        showCappingInMenu: {
            type: Boolean,
            default: true
        },
        countryLevel: {
            type: Boolean,
            default: true
        },
        onUpdate: {
            type: Boolean,
            default: false
        },
        tierIsUpdating: {
            type: Boolean,
            default: false
        },
        showChinaCountriesTooltip: {
            type: Boolean,
            default: false
        },
        sov: {
            default() {
                return {
                    val: 0,
                    maxVal: 0
                };
            }
        }
    },
    data() {
        const menuItems = [];
        if (this.showCappingInMenu) {
            menuItems.push({
                id: 'capping',
                name: this.getCappingMenuItemText(),
                iconData: { name: 'bellIcon', width: '14px', height: '15px' }
            });
        }
        menuItems.push({
            id: 'remove',
            name: 'Remove from Group',
            iconData: { name: 'deleteIcon', width: '12px', height: '15px' }
        });
        return {
            isHovering: false,
            menuItems,
            rateData: this.getRate(),
            defaultRatePlaceholder: '- - -',
            rateChangeOnGoing: false,
            rateChangeTimed: false,
            consts
        };
    },
    computed: {
        baseIconColor() {
            return consts.COLORS.LIGHT_600;
        },
        handleColor() {
            return consts.COLORS.LIGHT_600;
        },
        handleHoverColor() {
            return consts.COLORS.LIGHT_800;
        },
        missingConfigColor() {
            return consts.COLORS.NEGATIVE_500;
        },
        globeColor() {
            return consts.COLORS.LIGHT_700;
        },
        getProviderName() {
            if (this.tagId) {
                return consts.TAG_TYPE_TO_NAME[this.tagType];
            }

            return this.providerName;
        },
        activeCountriesTooltip() {
            const acTooltip = Countries.activeCountriesTooltip(
                this.isAllCountries,
                this.activeCountries,
                this.showChinaCountriesTooltip,
                this.tagId,
                this.tagType
            );

            return acTooltip;
        },
        showHandle() {
            return !this.viewOnly && !this.missingConfiguration && this.showMenu && this.isHovering;
        },
        isRateOverridden() {
            return (this.rates && !this.countryLevel && this.rates.countries !== undefined);
        },
        ratePlaceholder() {
            let rate = this.defaultRatePlaceholder;
            if (this.rates) {
                if (this.countryLevel && this.rates.group) {
                    rate = this.rates.group;
                } else if (this.rates.global) {
                    rate = this.rates.global;
                }
            }

            return rate;
        },
        hasRateValue() {
            return !!this.rateData || this.ratePlaceholder !== this.defaultRatePlaceholder;
        },
        impressionsComputed() {
            return this.impressions ? decimalFormat(this.impressions) : 'N/A';
        },
        ecpmComputed() {
            return this.ecpm ? `$ ${decimalFormat(this.ecpm)}` : 'N/A';
        }
    },
    watch: {
        allowRemoveFromGroup: {
            immediate: true,
            handler(newVal) {
                const removeFromGroup = this.menuItems.filter(
                    (option) => option.id === 'remove'
                )[0];
                removeFromGroup.disabled = !newVal;
            }
        },
        capping: {
            handler() {
                const cappingMenuItem = this.menuItems.filter(
                    (option) => option.id === 'capping'
                );
                if (cappingMenuItem.length) {
                    cappingMenuItem[0].name = this.getCappingMenuItemText();
                }
            }
        },
        rates: {
            handler() {
                this.rateData = this.getRate();
            }
        },
        tierIsUpdating: {
            handler(newVal) {
                if (!newVal && !this.rateChangeTimed) {
                    this.rateChangeOnGoing = false;
                }
            }
        }
    },
    validations: {
        rateData: {
            decimal,
            between: between(0.01, 1000)
        }
    },
    methods: {
        getRate() {
            if (this.rates) {
                if (this.countryLevel && this.rates.country) {
                    return this.rates.country;
                }

                if (!this.countryLevel && this.rates.group) {
                    return this.rates.group;
                }
            }
            return null;
        },
        getCappingMenuItemText() {
            return this.capping !== null
                ? `Cap: ${this.capping} / Session ${this.capping === 0 ? ' (No ad delivery)' : ''}`
                : 'Add Cap';
        },
        onRateChange(value) {
            this.rateChangeOnGoing = true;
            this.rateChangeTimed = true;
            this.$emit('rateChanged', {
                value,
                providerId: this.providerId,
                instanceId: this.instanceId,
                tagId: this.tagId,
                aniId: this.aniId
            });
            setTimeout(() => {
                this.rateChangeTimed = false;
                if (!this.tierIsUpdating) {
                    this.rateChangeOnGoing = false;
                }
            }, 3000);
        },
        onMenuActions(actionData) {
            let activeCountries = '';
            this.isHovering = false;
            if (!this.isAllCountries && (Array.isArray(this.activeCountries))) {
                activeCountries = { ...this.activeCountries };
            }
            this.$emit('menuAction', {
                actionData,
                instance: {
                    instanceId: this.instanceId,
                    providerId: this.providerId,
                    tagId: this.tagId,
                    isAllCountries: this.isAllCountries,
                    activeCountries,
                    providerName: this.providerName,
                    instanceName: this.name,
                    tierId: this.tierId,
                    iterationAdCap: this.capping
                }
            });
        }
    },
    components: {
        Tooltip,
        InlineLoader,
        InlineInput,
        IconBase,
        PercentBar,
        IconMenu
    }
};
</script>

<style scoped lang="scss">
    @import "~styles/styleGuide";

    .waffleGrid {
        border-left: 2px solid;
        display: grid;
        justify-content: space-between;
        grid-template-columns: 2.9fr 1.1fr 1fr 1fr .9fr;
        grid-gap: 15px;
        grid-template-rows: 40px;
        line-height: 40px;

        &:hover {
            background-color: $light-100;

            /deep/ .baseComponent {
                background-color: $light-300;
            }
        }

        /deep/ .baseComponent .override {
            padding-left: 6px;
        }
    }

    .dollarSign {
        padding-left: 2px;
    }

    .instanceMeta{
        display: grid;
        justify-content: stretch;
        grid-template-columns: minmax(23px, max-content) max-content minmax(auto, min-content) 16px;
        grid-template-rows: 40px;
        grid-column-gap: 5px;
    }

    .providerNameMissing {
        color: $negative-500;
    }

    .handle {
        margin-left: 6px;
        margin-top: -3px;
    }

    .lock{
        margin-left: 7px;
    }
    .counter {
        margin-left: 8px;
    }
    .middle {
        vertical-align: middle;
    }
    .rate{
        padding: 3px;
    }
    .ecpm {
        padding-left: 4px;
        padding-right: 4px;
    }
    .stat {
        padding-left: 4px;
        padding-right: 4px;
    }
    .more {
        cursor: pointer;
        right: 0px;
        border-radius: 100px;
        width: 30px;
        height: 30px;
        box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.15);
        border: solid 1px $light-500;
        background-color: $white;
        text-align: center;
        place-items: center;
    }

    .missingConfig  {
        cursor: pointer;
        margin-right: 9px;
        margin-top: 7px;
        float: right;
        height: 24px;
        width: 24px;
        display: inline-block;
        background-color: $negative-100;
        border-radius: 100%;
        vertical-align: middle;

        /deep/ svg {
            display: block;
            margin-top: 6px;
            margin-left: 6px;
        }
    }

    .missingConfigPopOver {
         width: 40px;
        @media #{$largeLaptopAndBelow} {
            display: none;
        }
     }

    .instanceName {
        vertical-align: middle;
    }

    .activeCountries {
        margin-left: 5px;
    }

    .instanceNameAndCountriesWrap {
        display: grid;
        justify-content: stretch;
        grid-template-columns: max-content auto;
    }

    .rateInheritedValue {
        /deep/ .inlineFont {
            input::placeholder, input::-webkit-input-placeholder, input:-ms-input-placeholder {
                @include font(6);
                color: $dark-300;
            }
            input:focus::placeholder {
                @include font(7);
                color: $light-800;
            }
        }
    }

    .light800 {
        color: $light-800;
    }

    .inlineLoader{
        margin-left: 6px;
        margin-top: -8px;
    }

    .actions {
        position: absolute;
        margin: 4px 4px 3px 20px;
        width: 30px;
        right: 6px;
    }

    .tooltip {
        width: 100%;
    }

    .sovData {
        display: inline-block;
        vertical-align: middle;
    }

    .values {
        padding-left: 7px;
    }

    .disableDrag {
        cursor: pointer;
        display: grid;
    }

    .menu-button {
        border-radius: 50%;
    }
</style>
